/**
 * "Talk to the category" — parse a sentence, or propose it as a DRAFT (band D).
 *
 * `POST …/chat` takes `execute` and, since band D, `draft`. This component sends only
 * `execute: false` (parse: report what would be written) and `draft: true` (file it as a pending
 * agent_actions row, written only when a super admin approves it in To decide). It has no way to
 * send `execute: true` — the one-click unreviewed write the route also offers stays on the
 * Category Research page.
 *
 * ⛔⛔ AND BOTH GO THROUGH `<Act>` LIKE EVERY OTHER CALL, BECAUSE BOTH SPEND.
 * The route calls `llmService.chat` BEFORE it looks at `execute` or `draft`, so a parse and a draft
 * each cost one model call whether or not anything comes of it (codex-reviewer, round 5). The
 * sentence is the confirmation's `ask`, the spend is its `cost`, and the reply lands in the same
 * result log as everything else.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { draftSentence, parseSentence } from '@/services/categoryCockpitActions';
import { Act, Acts, type ActionDef } from './Act';

const show = (v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v));

/** what the route understood, read out of its reply */
function Parsed({ body }: { body: Record<string, unknown> }) {
  const parsed = (body.parsed || null) as Record<string, unknown> | null;
  if (!parsed) {
    // The route answers 200 with no `parsed` when the model produced nothing usable. Saying so is
    // the point: an empty panel would read as "it understood and there is nothing to do".
    return <div className="pmk-warn">The route answered without a parsed operation — it did not understand the sentence as a change to this category. The call was made and paid for either way.</div>;
  }
  // the operation's own fields: without them the parse of an equivalence or a rule said nothing
  // about WHAT would be written
  const fields: Array<[string, unknown]> = [
    ['attribute', parsed.attributeName], ['mappings', parsed.mappings], ['rules', parsed.rules],
    ['rule type', parsed.ruleType], ['rule', parsed.ruleData],
  ];
  return (
    <dl className="pmk-kv">
      <dt>operation</dt><dd><code>{String(parsed.operation || '—')}</code></dd>
      {parsed.conceptType || parsed.conceptName ? <><dt>on</dt><dd>{String(parsed.conceptType || '—')} · <b>{String(parsed.conceptName || '—')}</b></dd></> : null}
      {fields.filter(([, v]) => v != null && v !== '').map(([k, v]) => <React.Fragment key={k}><dt>{k}</dt><dd><code>{show(v)}</code></dd></React.Fragment>)}
      {parsed.context ? <><dt>context</dt><dd>{String(parsed.context)}</dd></> : null}
      {parsed.confirmationMessage ? <><dt>it would say</dt><dd>{String(parsed.confirmationMessage)}</dd></> : null}
      <dt>applied?</dt><dd>No. This was a read — use <b>Propose as a draft</b> to file it for approval.</dd>
    </dl>
  );
}

/** the pending draft the route filed */
export function Drafted({ body }: { body: Record<string, unknown> }) {
  const d = (body.draft || (body.data as Record<string, unknown> | undefined) || null) as Record<string, unknown> | null;
  if (!d || !d.action_id) {
    return <div className="pmk-warn">The route answered without a draft id — nothing says a draft was filed. Check To decide before proposing it again.</div>;
  }
  return (
    <dl className="pmk-kv">
      <dt>draft</dt><dd><code>{String(d.action_type)}</code> · {String(d.summary || '')}</dd>
      <dt>status</dt><dd>{String(d.status || 'pending')} — nothing is written to the category until a super admin approves it</dd>
      <dt>where</dt><dd><Link to="/category-cockpit/decide">To decide → Knowledge drafts</Link> · <span className="pmc-mute">agent_actions {String(d.action_id)}</span></dd>
    </dl>
  );
}

export function parseAction(sectorId: number, path: string): ActionDef {
  return {
    label: 'Parse a sentence about this category',
    confirm: {
      title: 'Parse a sentence',
      verb: 'Parse',
      ask: { label: 'What do you want to say about this category?', placeholder: 'e.g. "gravel bikes are not the same use case as road bikes"', min: 3 },
      writes: <><b>nothing.</b> It is sent with <code>execute: false</code>: the reply says what would be written.</>,
      cost: <><b>one model call.</b> The route calls the model before it looks at <code>execute</code>, so a parse costs the same whether or not anything would be written.</>,
    },
    run: (sentence) => parseSentence(sectorId, path, sentence),
    render: (r) => <Parsed body={r.body} />,
  };
}

/**
 * The draft action, shared by the chat and by the judge's "Apply" on a finding (which pre-fills
 * the sentence and records `from: 'judge'`).
 */
export function draftAction(sectorId: number, path: string, siteId: string | null, opts: { label?: string; title?: string; initial?: string; from?: 'chat' | 'judge' } = {}): ActionDef {
  return {
    label: opts.label || 'Propose as a draft',
    op: `draft:${opts.from || 'chat'}:${sectorId}:${path}:${opts.initial || ''}`,
    disabled: siteId ? undefined : 'Select a site in the header: a draft is filed under a site of this sector',
    confirm: {
      title: opts.title || 'Propose a change as a draft',
      verb: 'Draft',
      ask: {
        label: opts.initial ? 'The change, as a sentence — edit it before drafting' : 'What should change in this category?',
        placeholder: 'e.g. "cushioning: Cloudfoam=moderate, BOOST=maximum"',
        min: 3,
        initial: opts.initial,
      },
      writes: <>one <b>pending</b> row in <code>agent_actions</code> (a knowledge draft), filed under the selected site. <b>The category is not touched</b>: the change is written only when a super admin approves it in To decide, and that write records the previous value in <code>category_characteristics_research_history</code>.</>,
      cost: <><b>one model call</b> to parse the sentence, paid whether or not a draft comes out of it. Approving an <i>add</i> or <i>correct</i> of a concept later runs its research — more model calls then.</>,
    },
    run: (sentence) => {
      if (!siteId) return Promise.reject(new Error('no site selected'));
      return draftSentence(sectorId, path, sentence, siteId, opts.from || 'chat');
    },
    render: (r) => <Drafted body={r.body} />,
  };
}

export function Chat({ sectorId, path, siteId = null }: { sectorId: number; path: string; siteId?: string | null }) {
  return (
    <>
      <Acts>
        <Act def={parseAction(sectorId, path)} />
        <Act def={draftAction(sectorId, path, siteId)} />
      </Acts>
      <div className="pmc-small pmc-mute">
        Parse reads only. Propose as a draft files the change for approval in To decide — nothing is
        written to the category until a super admin approves it. Both spend one model call.
      </div>
    </>
  );
}
